/**
 * Reading members out of a mounted DAT1 archive — Fallout 1's container.
 *
 * `archive.ts` reads DAT2, which is Fallout 2's format and nothing like this
 * one. DAT1 is the older Interplay `plib` "database": the directory lives at the
 * *front* of the file, every integer is big-endian, and the payload codec is
 * Interplay's own LZSS rather than zlib. On an FO1 install every file the save
 * editor wants — `proto\items\items.lst`, the `.pro` files, `pro_item.msg`, the
 * inventory `.frm`s — is inside `MASTER.DAT` in this format, so without a reader
 * the Inventory tab has nothing to show and says so.
 *
 * ## The container
 *
 * The whole directory is two levels of the same `assoc_array` serialization
 * (`assoc_load`, plib/assoc/assoc.cc:396), which is:
 *
 *     size:i32be  max:i32be  datasize:i32be  list_ptr:i32be   ← header, 16 bytes
 *     then `size` times: nameLen:u8, name (nameLen bytes), <datasize bytes>
 *
 * All four header ints are big-endian because `assoc_read_long`
 * (assoc.cc:333) shifts a byte at a time from the high end; `db_read_long`
 * (plib/db/db.cc:1502) does the same for the entry payload. There is no magic
 * number anywhere in the file.
 *
 * `db_init_database` (db.cc:1925-1988) loads it twice over:
 *
 *   1. the **root** array, whose `datasize` is 0, so its entries are just names
 *      — the directory names, backslash-separated and upper-cased, e.g.
 *      `ART\INTRFACE`. The root's first entry is `.`, the archive's top level.
 *   2. then, in root order, one array *per directory*, whose `datasize` is 16
 *      and whose per-entry payload is a `dir_entry` (db.h:13-18) read by
 *      `db_assoc_load_dir_entry` (db.cc:1776-1788):
 *
 *          flags:i32be  offset:i32be  length:i32be  field_C:i32be
 *
 *      `length` is the unpacked size, `field_C` the packed size on disk.
 *
 * A member's full path is the directory name, a backslash, and the entry name
 * (`db_find_dir_entry`, db.cc:2495-2545, splits the request at the last
 * backslash and looks the halves up in exactly those two arrays). The `.` root
 * directory contributes its entries at the top level with no prefix.
 *
 * ## The three storage modes
 *
 * `db_read_to_buf` (db.cc:445-513) switches on `flags & 0xF0`, treating a flags
 * value of 0 as 16:
 *
 *   - **0x10** — one LZSS stream. `lzss_decode_to_buf(stream, buf, de.field_C)`:
 *     the argument is an *input* budget, not an output length.
 *   - **0x20** — stored. `length` bytes copied straight out.
 *   - **0x40** — blocked LZSS. Repeat until `length` bytes have been produced:
 *     read a big-endian u16 (`fread_short`, db.cc:2721 — high byte first); if
 *     bit 0x8000 is set the low 15 bits are a count of *raw* bytes to copy,
 *     otherwise the u16 is the compressed size of one LZSS block. This is the
 *     mode almost everything in master.dat actually uses.
 *
 * ## The LZSS variant
 *
 * `lzss.cc:39-125`. A 4096-byte ring buffer pre-filled with spaces and seeded at
 * index 4078; a flag byte whose bits are consumed low-to-high, a set bit meaning
 * one literal byte and a clear bit meaning a two-byte back-reference
 * `(low | ((high & 0xF0) << 4), (high & 0x0F) + 3)` — a 12-bit ring offset and a
 * length of 3..18 (`lzss_decode_chunk_to_buf`, lzss.cc:270-297). Every byte
 * emitted, literal or copied, is also written back into the ring. Decoding stops
 * when the input budget is spent, which is why the caller must know the packed
 * size — there is no end marker.
 *
 * ## Why this is not just `archive.ts` with a different parser
 *
 * Two structural differences force a separate module. The directory is not a
 * single tail block, so it is walked with a forward-only buffered cursor rather
 * than one ranged read. And the codec is synchronous, so a member read never
 * awaits — the `Promise` in the shared `DatArchive` interface is there for
 * DAT2's `DecompressionStream`, and DAT1 simply resolves immediately.
 *
 * Like `archive.ts`, nothing here throws: a malformed archive yields null and
 * the editor degrades to an empty catalog rather than a panel that will not open.
 */

import type { EmFS } from '../engine';
import type { DatArchive, DatEntry } from './archive';

/** Members bigger than this are not something the editor asks for. */
const MAX_MEMBER_BYTES = 32 * 1024 * 1024;
const MAX_DIR_COUNT = 4096;
const MAX_ENTRIES_PER_DIR = 65536;
const MAX_FILE_COUNT = 500_000;
/** The directory region of FO1's master.dat is ~1MB; anything past this is junk. */
const MAX_TREE_BYTES = 32 * 1024 * 1024;
const CHUNK = 1 << 20;

const normalize = (member: string): string =>
  member.replace(/\\/g, '/').replace(/^\.\//, '').replace(/^\/+/, '').toLowerCase();

type RangeReader = (start: number, length: number) => Uint8Array | Promise<Uint8Array>;

/**
 * A forward-only cursor over the archive, refilling from the ranged reader as
 * the directory walk needs more. The tree's length is not knowable until it has
 * been parsed, so it cannot be read in one shot.
 */
class Cursor {
  private buf: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
  /** File offset of `buf[0]`. */
  private base = 0;
  /** Read position, as a file offset. */
  pos = 0;

  private readonly readRange: RangeReader;
  private readonly size: number;

  constructor(readRange: RangeReader, size: number) {
    this.readRange = readRange;
    this.size = size;
  }

  /** Guarantees `n` bytes are buffered at `pos`. False at end-of-file. */
  private async want(n: number): Promise<boolean> {
    if (this.pos + n > this.size) return false;
    const have = this.base + this.buf.length - this.pos;
    if (have >= n) return true;
    const start = this.pos;
    const length = Math.min(Math.max(n, CHUNK), this.size - start);
    const got = await this.readRange(start, length);
    if (got.length < n) return false;
    this.buf = got;
    this.base = start;
    return true;
  }

  private at(off: number): number {
    return this.buf[this.pos - this.base + off]!;
  }

  async u8(): Promise<number | null> {
    if (!(await this.want(1))) return null;
    const v = this.at(0);
    this.pos += 1;
    return v;
  }

  /** Big-endian int32 — `db_read_long` / `assoc_read_long`. */
  async i32(): Promise<number | null> {
    if (!(await this.want(4))) return null;
    const v = ((this.at(0) << 24) | (this.at(1) << 16) | (this.at(2) << 8) | this.at(3)) | 0;
    this.pos += 4;
    return v;
  }

  async ascii(len: number): Promise<string | null> {
    if (len === 0) return '';
    if (!(await this.want(len))) return null;
    let s = '';
    for (let i = 0; i < len; i++) {
      const b = this.at(i);
      // The engine's names are plain upper-case ASCII. A non-printable byte
      // means we are not where we think we are in the file.
      if (b < 0x20 || b > 0x7e) return null;
      s += String.fromCharCode(b);
    }
    this.pos += len;
    return s;
  }
}

/** The `assoc_array` header: size, max, datasize (list_ptr is read and dropped). */
async function readAssocHeader(
  c: Cursor
): Promise<{ size: number; max: number; datasize: number } | null> {
  const size = await c.i32();
  const max = await c.i32();
  const datasize = await c.i32();
  const listPtr = await c.i32();
  if (size === null || max === null || datasize === null || listPtr === null) return null;
  if (size < 0 || max < size) return null;
  return { size, max, datasize };
}

/**
 * Interplay's LZSS, decoding into `dest` from `src[srcPos]` with an input budget
 * of `budget` bytes. Returns the number of bytes produced, or -1 if the stream
 * ran off either end. A direct port of `lzss_decode_to_buf` (lzss.cc:39).
 */
function lzssDecode(
  src: Uint8Array,
  srcPos: number,
  budget: number,
  dest: Uint8Array,
  destPos: number
): number {
  const ring = new Uint8Array(4096).fill(0x20);
  let ringIndex = 4078;
  let i = srcPos;
  const srcEnd = srcPos + budget;
  let out = destPos;
  let left = budget;

  while (left > 0) {
    if (i >= srcEnd || i >= src.length) return -1;
    const flags = src[i++]!;
    left -= 1;
    for (let bit = 0; bit < 8 && left > 0; bit++) {
      if ((flags & (1 << bit)) !== 0) {
        if (i >= srcEnd) return -1;
        left -= 1;
        const byte = src[i++]!;
        if (out >= dest.length) return -1;
        dest[out++] = byte;
        ring[ringIndex] = byte;
        ringIndex = (ringIndex + 1) & 0xfff;
      } else {
        if (i + 1 >= srcEnd) return -1;
        left -= 2;
        const low = src[i++]!;
        const high = src[i++]!;
        const offset = low | ((high & 0xf0) << 4);
        const runLength = (high & 0x0f) + 3;
        for (let k = 0; k < runLength; k++) {
          const byte = ring[(offset + k) & 0xfff]!;
          if (out >= dest.length) return -1;
          dest[out++] = byte;
          ring[ringIndex] = byte;
          ringIndex = (ringIndex + 1) & 0xfff;
        }
      }
    }
  }
  return out - destPos;
}

/**
 * The 0x40 mode: a sequence of `[u16be][block]` until `dest` is full. The high
 * bit of the word means the block is stored, not compressed.
 * `db_read_to_buf` case 64, db.cc:479-512.
 */
function decodeBlocked(src: Uint8Array, dest: Uint8Array): boolean {
  let i = 0;
  let out = 0;
  while (out < dest.length) {
    if (i + 2 > src.length) return false;
    const word = ((src[i]! << 8) | src[i + 1]!) & 0xffff;
    i += 2;
    if ((word & 0x8000) !== 0) {
      const n = word & 0x7fff;
      if (i + n > src.length || out + n > dest.length) return false;
      dest.set(src.subarray(i, i + n), out);
      i += n;
      out += n;
    } else {
      const produced = lzssDecode(src, i, word, dest, out);
      if (produced < 0) return false;
      i += word;
      out += produced;
    }
  }
  return out === dest.length;
}

/** Opens a mounted DAT1. Returns null for anything that is not one. */
export async function openDat1Archive(FS: EmFS, path: string): Promise<DatArchive | null> {
  let size: number;
  try {
    size = FS.stat(path).size;
  } catch {
    return null;
  }
  if (typeof FS.open !== 'function' || typeof FS.read !== 'function' || typeof FS.close !== 'function') {
    return null;
  }
  const readRange = (start: number, length: number): Uint8Array => {
    const out = new Uint8Array(length);
    if (length === 0) return out;
    const stream = FS.open!(path, 'r');
    try {
      let got = 0;
      while (got < length) {
        const n = FS.read!(stream, out, got, length - got, start + got);
        if (n <= 0) break;
        got += n;
      }
      return got === length ? out : out.subarray(0, got);
    } finally {
      FS.close!(stream);
    }
  };
  return openDat1ArchiveCore(path, size, readRange);
}

/** The same over a Blob — the vault's storage form, no filesystem involved. */
export function openDat1ArchiveBlob(path: string, blob: Blob): Promise<DatArchive | null> {
  return openDat1ArchiveCore(path, blob.size, async (start, length) => {
    return new Uint8Array(await blob.slice(start, start + length).arrayBuffer());
  });
}

export async function openDat1ArchiveCore(
  path: string,
  size: number,
  readRange: RangeReader
): Promise<DatArchive | null> {
  if (size < 1024) return null;

  const c = new Cursor(readRange, size);
  const root = await readAssocHeader(c);
  // The root carries directory *names* only: `assoc_init(&root, 0, …)` is
  // overwritten by the file's own datasize, which is 0 in every real archive.
  if (!root || root.datasize !== 0 || root.size === 0 || root.size > MAX_DIR_COUNT) return null;

  const dirNames: string[] = [];
  for (let i = 0; i < root.size; i++) {
    const len = await c.u8();
    if (len === null || len === 0 || len > 200) return null;
    const name = await c.ascii(len);
    if (name === null) return null;
    dirNames.push(name);
  }

  const entries = new Map<string, DatEntry>();
  for (const dirName of dirNames) {
    const head = await readAssocHeader(c);
    if (!head) return null;
    // Every per-directory array carries a 16-byte `dir_entry` per name.
    if (head.datasize !== 16 || head.size > MAX_ENTRIES_PER_DIR) return null;
    // `.` is the archive's top level and contributes no path prefix.
    const prefix = dirName === '.' || dirName === '' ? '' : `${dirName}\\`;

    for (let j = 0; j < head.size; j++) {
      const len = await c.u8();
      if (len === null || len === 0 || len > 200) return null;
      const name = await c.ascii(len);
      if (name === null) return null;
      const flags = await c.i32();
      const offset = await c.i32();
      const length = await c.i32();
      const packedLength = await c.i32();
      if (flags === null || offset === null || length === null || packedLength === null) return null;
      if (offset < 0 || length < 0 || packedLength < 0) return null;
      if (offset > size) return null;
      entries.set(normalize(prefix + name), {
        // "packed" in the DAT2 sense: the bytes on disk are not the bytes out.
        packed: (flags & 0xf0) !== 0x20,
        realSize: length,
        packedSize: packedLength,
        offset,
        // DAT1 has three modes where DAT2 has two, so the raw value is kept.
        dat1Flags: flags,
      });
      if (entries.size > MAX_FILE_COUNT) return null;
    }
    if (c.pos > MAX_TREE_BYTES || c.pos > size) return null;
  }

  if (entries.size === 0) return null;
  const sorted = [...entries.keys()].sort();

  return {
    path,
    entries,
    has: (member) => entries.has(normalize(member)),
    list: (prefix) => {
      const want = normalize(prefix);
      return sorted.filter((k) => k.startsWith(want));
    },
    async read(member) {
      const e = entries.get(normalize(member));
      if (!e) return null;
      if (e.realSize > MAX_MEMBER_BYTES) return null;
      // `flags == 0` means mode 0x10 — db_read_to_buf, db.cc:445-447.
      const mode = (e.dat1Flags ?? 0) === 0 ? 0x10 : (e.dat1Flags! & 0xf0);
      const onDisk = mode === 0x20 ? e.realSize : e.packedSize;
      if (onDisk > MAX_MEMBER_BYTES || e.offset + onDisk > size) return null;
      const raw = await readRange(e.offset, onDisk);
      if (raw.length !== onDisk) return null;

      if (mode === 0x20) return raw;

      const out = new Uint8Array(e.realSize);
      if (mode === 0x40) {
        return decodeBlocked(raw, out) ? out : null;
      }
      if (mode === 0x10) {
        const produced = lzssDecode(raw, 0, onDisk, out, 0);
        // A member whose decoded length disagrees with the directory is one we
        // read at the wrong offset. Refusing beats handing back half a proto.
        return produced === e.realSize ? out : null;
      }
      return null;
    },
  };
}
